<template>
  <div class="login-container">
    <header class="header">
      <router-link to="/">
        <div class="logo">
          <span v-html="logoZerek" />
          <div class="logo-text">
            <p class="logo-text-title">ZEREK</p>
            <p class="logo-text-subtitle1">
              {{ $t("landing.zerekTitlePart1") }}
            </p>
            <p class="logo-text-subtitle2">
              {{ $t("landing.zerekTitlePart2") }}
            </p>
          </div>
        </div>
      </router-link>
      <LanguageDropdown :languages="languages" />
    </header>

    <div class="profile-main-wrapper">
      <ProfileForm
        v-if="!isLoading"
        ref="profileForm"
        :form-data="form"
        :doc-type-items="docType"
        :issued-by-items="issuedBy"
        :loading="isSubmitLoading"
        :is-submit-loading="isSubmitLoading"
        @submit="handleSubmitForm"
        @update:form-data="form = $event"
        @validation-error="handleValidationError"
      />
      
      <ProgressCircular
        :isSubmitLoading="isSubmitLoading"
        v-if="isLoading || isSubmitLoading"
      />
    </div>
  </div>
</template>

<script>
import LanguageDropdown from "@/components/custom/LanguageDropdown.vue";
import ProfileForm from "@/components/profile/ProfileForm.vue";
import ProgressCircular from "@/components/custom/ProgressCircular.vue";
import logoZerek from "!!raw-loader!@/assets/logo_zerek.svg";
import { mapActions, mapGetters } from "vuex";
import { showToast } from "@/utils/toast";
import { saveProfile } from "@/api/profile";
import Cookies from "js-cookie";

export default {
  name: "RegistrationStepper",
  components: { 
    LanguageDropdown, 
    ProfileForm, 
    ProgressCircular 
  },
  
  data() {
    return {
      logoZerek,
      languages: [
        { code: "kk", name: "Қазақша" },
        { code: "ru", name: "Русский" },
      ],
      isLoading: false,
      isSubmitLoading: false,
      form: {
        name: "",
        docType: "",
        email: "",
        docNumber: "",
        workplace: "",
        issuedBy: "",
        phone: "",
        issueDate: "",
        additionalPhone: "",
      },
    };
  },
  
  created() {
    this.initializePageData();
  },
  
  mounted() {
    // Инициализация формы из Vuex
    this.initializeFormFromUser();
  },
  
  methods: {
    ...mapActions({
      fetchProfileData: "profileData/loadProfileData",
      fetchIssuedByData: "issuedBy/loadIssuedByData",
      fetchDocumentTypeData: "documentType/loadDocumentTypeData",
    }),
    
    async initializePageData() {
      if (this.isLoading) return;

      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchProfileData(),
          this.fetchIssuedByData(),
          this.fetchDocumentTypeData(),
        ]);
        this.initializeFormFromUser();
      } catch (error) {
        showToast(this, {
          text: `${this.$t(`toastText.errorData`)}: ${error.message || error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    initializeFormFromUser() {
      if (this.user) {
        // Форматируем телефонные номера для отображения
        this.form = {
          ...this.user,
          phone: this.formatPhoneNumber(this.user.phone),
          additionalPhone: this.user.additionalPhone ? 
            this.formatPhoneNumber(this.user.additionalPhone) : 
            "",
        };
      }
    },
    
    // Добавляем метод formatPhoneNumber
    formatPhoneNumber(number) {
      if (!number) return "";
      
      // Преобразуем номер в строку и удаляем все нецифровые символы
      let clean = number.toString().replace(/\D/g, "");

      // Нормализация номера
      if (clean.startsWith("8")) {
        clean = "7" + clean.slice(1);
      } else if (clean.startsWith("07")) {
        clean = "7" + clean.slice(1);
      } else if (clean.length === 10 && !clean.startsWith("7")) {
        clean = "7" + clean;
      }

      // Форматируем в маску +7(XXX) XXX-XX-XX
      if (clean.length === 11 && clean.startsWith("7")) {
        return clean.replace(
          /^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/,
          "+$1($2) $3-$4-$5"
        );
      }
      
      // Возвращаем оригинальный номер, если формат не распознан
      return number;
    },
    
    normalizePhone(phone) {
      if (!phone) return '';
      // Убираем все нецифровые символы
      const digits = phone.toString().replace(/\D/g, '');
      
      // Если номер начинается с 8, заменяем на 7
      if (digits.startsWith('8')) {
        return '7' + digits.slice(1);
      }
      
      // Если номер начинается с 7 и имеет 11 цифр, возвращаем как есть
      if (digits.startsWith('7') && digits.length === 11) {
        return digits;
      }
      
      // Если номер 10 цифр, добавляем 7 в начало
      if (digits.length === 10) {
        return '7' + digits;
      }
      
      return digits;
    },
    
    async handleSubmitForm(submissionData) {
      this.isSubmitLoading = true;
      
      try {
        const payload = {
          ...submissionData,
          phone: this.normalizePhone(submissionData.phone),
          additionalPhone: submissionData.additionalPhone ? 
            this.normalizePhone(submissionData.additionalPhone) : 
            null,
        };
        delete payload.agreement;
        await saveProfile(payload);
        
        showToast(this, {
          text: this.$t(`toastText.successfullySaved`),
          status: "success",
          duration: 3000,
        });
        
        Cookies.set("first_entry", "false");
        setTimeout(() => {
          this.$router.push('/profile');
        }, 1500);
        
      } catch (error) {
        
        let errorMessage = this.$t(`toastText.saveError`);
        
        if (error.response) {
          
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else {
            switch (error.response.status) {
              case 400:
                errorMessage = this.$t(`toastText.validationError`);
                break;
              case 409:
                errorMessage = this.$t(`toastText.userExists`);
                break;
              case 500:
                errorMessage = this.$t(`toastText.serverError`);
                break;
            }
          }
        }
        
        showToast(this, {
          text: errorMessage,
          status: "error",
          duration: 5000,
        });
      } finally {
        this.isSubmitLoading = false;
      }
    },
    
    handleValidationError(errorData) {
      console.warn("Validation error:", errorData);
      showToast(this, {
        text: this.$t(`toastText.fields`),
        status: "warning",
        duration: 3000,
      });
    }
  },
  
  computed: {
    ...mapGetters("profileData", ["user"]),
    ...mapGetters("issuedBy", ["issuedBy"]),
    ...mapGetters("documentType", ["docType"]),
  },
  
  watch: {
    user: {
      handler(val) {
        if (val) {
          this.initializeFormFromUser();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
::v-deep.logo span svg {
  width: 40px;
  height: 40px;
}
.login-container {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f8ff;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.header {
  width: 90%;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
}
.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}
.logo-text-title {
    font-size: 16px;
    margin: 0;
    font-weight: bold;
    color: #000;
  }

.logo-text-subtitle1,
.logo-text-subtitle2 {
  font-size: 14px;
  margin: 0;
  line-height: 1.2;
  color: #666;
}

.profile-main-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin:auto;
}
.profile-card-info {
  width: 950px;
  padding: 40px !important;
}

@media (max-width: 768px) {
  .profile-main-wrapper {
    margin: 20px 0;
    width: 80%;
  }
  .profile-card-info {
    padding: 20px;
  }
  .profile-actions {
    justify-content: center;
  }
  .profile-forms-row {
    flex-direction: column;
  }
  .profile-form-field {
    max-width: 100%;
  }
  .v-btn.profile-save-btn {
    padding: 25px 20px;
    margin: 0px auto;
    margin-top: 20px;
  }
}

@media (max-width: 640px) {
  .logo-text-title {
    font-size: 14px;
    margin: 0;
    font-weight: bold;
  }

  .logo-text-subtitle1,
  .logo-text-subtitle2 {
    font-size: 12px;
    margin: 2px 0;
    line-height: 1.2;
  }
}

@media (max-width: 480px) {
  .profile-actions {
    flex-direction: column;
    gap: 10px;
  }
  .v-btn.profile-cancel-btn {
    margin-right: unset;
  }
  .profile-card-info h2{
    text-align: center;
  }
}
</style>